/**
 * WelcomeGate — the first thing a signed-out user sees when they open
 * the NATIVE app's home tab.
 *
 * Previously a fresh app open (no account) landed straight on the
 * public web-style network feed — fine for a browser visitor arriving
 * via a shared link, but confusing for someone who just downloaded
 * the app from the App Store with no context. This replaces that
 * feed with a real "Welcome to FLYT" moment and a hard choice: Sign
 * Up or Sign In. From either button, the EXISTING flow takes over
 * unchanged — LoginModal's real signup form (name/email/password; DOB
 * moved to the universal post-login DobGuard), then PersonaWelcomeModal's
 * persona picker, then (for Athlete) AthleteOnboardingFlow's quick,
 * fully-skippable onboarding (sport, link, photo).
 *
 * Scope: native app's home tab ONLY. Shared links (story pages,
 * upload-invite links, athlete profile pages) are separate routes
 * that never render this — they stay open to anonymous visitors on
 * both web and native, which is what keeps them shareable at all.
 */
import { createPortal } from 'react-dom';
import { FlytWings } from './FlytWings';
import { useUserAuth } from '../contexts/UserAuthContext';
const WelcomeGate = () => {
const { setLoginOpen, setLoginMode, openSignup } = useUserAuth();
const handleSignIn = () => {
setLoginMode('login');
setLoginOpen(true);
};
// Portalled straight to <body> — this is rendered deep inside the
// PageTransition/ChannelTransition wrappers, which apply their own
// transform during route animation and quietly trap any z-index set
// here inside that local stacking context. Without the portal, the
// globally-mounted <BottomNav/> (a later DOM sibling at the app-shell
// level) paints on top regardless of z-index, which is what was
// hiding the Sign In button. AthleteOnboardingFlow.jsx uses the same
// fix for the same reason.
//
// z-[100] — deliberately just BELOW LoginModal/PersonaWelcomeModal
// (both z-[120], also portalled to <body>). Sign Up/Sign In both just
// call setLoginOpen(true); LoginModal still needs to render on TOP of
// this screen once tapped. Using a very high value here (this used to
// be z-[9600], harmless before the portal because it was trapped and
// never actually compared against anything — see above) made every
// tap silently open LoginModal *behind* this opaque background,
// looking like the buttons did nothing at all.
return createPortal(
<div
className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-white px-8"
style={{ background: 'radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.92) 55%), #050505' }}
data-testid="welcome-gate"
>
{/* Sign Up / Sign In sit directly under the wordmark now, as one
          vertically-centered group, instead of the heading being pinned
          near the top with the buttons pinned to the very bottom. */}
<div className="w-full max-w-sm flex flex-col items-center">
<FlytWings className="w-14 h-14 mb-6" />
<h1 className="font-display text-4xl uppercase text-center leading-[0.95]">Welcome to FLYT.</h1>
<p className="mt-3 text-white/60 text-sm text-center leading-relaxed">
          Your athlete operating system.
</p>
<div className="w-full mt-10 space-y-3">
<button
type="button"
onClick={openSignup}
className="w-full inline-flex items-center justify-center px-6 py-4 rounded-full bg-white text-black font-black text-[13px] uppercase tracking-widest hover:bg-white/90 transition active:scale-[0.99]"
data-testid="welcome-gate-signup"
>
            Sign Up
</button>
<button
type="button"
onClick={handleSignIn}
className="w-full inline-flex items-center justify-center px-6 py-4 rounded-full bg-white/[0.06] ring-1 ring-white/15 backdrop-blur-md text-white font-black text-[13px] uppercase tracking-widest hover:bg-white/[0.1] hover:ring-white/25 transition active:scale-[0.99]"
data-testid="welcome-gate-signin"
>
            Sign In
</button>
</div>
</div>
</div>,
    document.body,
);
};
export default WelcomeGate;
